import React, { useEffect, useRef } from 'react';

// How a chat bot's messages appear — /meet (pages/AIOnboarding.js) and the
// demo (pages/Demo.js): a few words at a time, each fading in (AIOnboarding.css
// .aio-chunk), with **bold** for the key words.

// Each new bot message arrives in quick chunks — a few words at a time,
// each fading in — after a short flash of "typing…". A long one takes about
// 1.4s at most.
export const CHUNK_WORDS = 3;
const CHUNK_MS = 70;
const CHUNKS_MAX_MS = 1400;
export const wordCount = (text) => (String(text || '').match(/\S+/g) || []).length;
export const chunkMs = (text) => Math.min(CHUNK_MS, CHUNKS_MAX_MS / Math.max(1, Math.ceil(wordCount(text) / CHUNK_WORDS)));
export const typeDuration = (text) => Math.ceil(wordCount(text) / CHUNK_WORDS) * chunkMs(text) + 250;

// A message's text as paragraphs, line breaks and **bold**. With `chunked`
// ({ step }), every word fades in, CHUNK_WORDS at a time, step ms apart.
export const renderMessageText = (content, chunked = null) => {
  let word = 0;
  const words = (text, bold) => text.split(/(\s+)/).map((piece, i) => {
    if (!chunked || !piece.trim()) return piece;
    const delay = Math.floor(word / CHUNK_WORDS) * chunked.step;
    word += 1;
    const el = <span key={i} className="aio-chunk" style={{ animationDelay: `${delay}ms` }}>{piece}</span>;
    return bold ? <strong key={i}>{el}</strong> : el;
  });
  const line = (text) => text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (
    part.startsWith('**') && part.endsWith('**')
      ? (chunked ? <span key={i}>{words(part.slice(2, -2), true)}</span> : <strong key={i}>{part.slice(2, -2)}</strong>)
      : <span key={i}>{chunked ? words(part, false) : part}</span>
  ));
  return content.split('\n\n').map((paragraph, pi) => (
    <p key={pi} className="demo-paragraph">
      {paragraph.split('\n').map((l, li, lines) => (
        <span key={li}>
          {line(l)}
          {li < lines.length - 1 && <br />}
        </span>
      ))}
    </p>
  ));
};

// A new bot message arriving in quick chunks; onDone once it's all shown.
export const ChunkReveal = ({ text, onDone }) => {
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  useEffect(() => {
    const id = setTimeout(() => doneRef.current?.(), typeDuration(text));
    return () => clearTimeout(id);
  }, [text]);
  return <>{renderMessageText(text, { step: chunkMs(text) })}</>;
};
